import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { shopApi } from '../services/api';
import { C, fmt } from './ShopLayout';

function Stars({ rating, size = 14 }) {
  return (
    <span style={{ fontSize: size }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= Math.round(rating) ? '#F5A623' : '#ddd' }}>★</span>
      ))}
    </span>
  );
}

export default function ShopProduct() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [imgIndex, setImgIndex] = useState(0);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setLoading(true);
    shopApi.product(slug).then(setProduct).catch(() => setProduct(null)).finally(() => setLoading(false));
  }, [slug]);

  function addToCart() {
    const variantStr = Object.entries(selectedVariants).map(([k,v]) => `${k}: ${v}`).join(', ');
    add(product, qty, variantStr);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: C.muted }}>Načítám…</div>;
  if (!product) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
      <div style={{ fontSize: 18, color: C.muted, marginBottom: 20 }}>Produkt nenalezen</div>
      <Link to="/shop" style={{ color: C.primary }}>← Zpět do obchodu</Link>
    </div>
  );

  const images = [...new Set([product.image_url, ...(product.images || [])].filter(Boolean))];
  const inStock = product.stock_quantity > 0;
  const variantGroups = (product.variants || []).reduce((acc, v) => {
    if (!acc[v.name]) acc[v.name] = [];
    acc[v.name].push(v);
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 48px' }}>
      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, display: 'flex', gap: 6, alignItems: 'center' }}>
        <Link to="/shop" style={{ color: C.muted, textDecoration: 'none' }}>Obchod</Link>
        {product.category && <><span>›</span><Link to={`/shop?category=${encodeURIComponent(product.category)}`} style={{ color: C.muted, textDecoration: 'none' }}>{product.category}</Link></>}
        <span>›</span><span style={{ color: C.text }}>{product.name}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }}>
        {/* Fotky */}
        <div>
          <div style={{ background: '#f7f7f7', borderRadius: 12, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 380, position: 'relative' }}>
            {images.length > 0
              ? <img src={images[imgIndex]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <span style={{ fontSize: 100 }}>📦</span>}
            {product.source_url && <span style={{ position: 'absolute', top: 12, left: 12, background: '#E6F1FB', color: '#185FA5', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4 }}>Z FB</span>}
          </div>
          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {images.map((img, i) => (
                <img key={i} src={img} alt="" onClick={() => setImgIndex(i)}
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: `2px solid ${i === imgIndex ? C.primary : C.border}`, opacity: i === imgIndex ? 1 : 0.65 }} />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {product.category && <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{product.category}</div>}
          <h1 style={{ fontSize: 22, fontWeight: 500, color: C.text, margin: '0 0 10px', lineHeight: 1.3 }}>{product.name}</h1>

          {product.review_count > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <Stars rating={product.avg_rating} />
              <span style={{ fontSize: 13, color: C.muted }}>{product.avg_rating} ({product.review_count} hodnocení)</span>
            </div>
          )}

          <div style={{ fontSize: 28, fontWeight: 700, color: C.primary, marginBottom: 4 }}>
            {fmt(product.price_display || product.price_sell)}
          </div>
          {product.vat_registered && <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>vč. DPH {product.vat_rate}%</div>}
          {!product.vat_registered && <div style={{ marginBottom: 16 }} />}

          {/* Varianty */}
          {Object.entries(variantGroups).map(([groupName, variants]) => (
            <div key={groupName} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{groupName}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {variants.map(v => {
                  const isSelected = selectedVariants[groupName] === v.value;
                  return (
                    <button key={v.id} onClick={() => setSelectedVariants(prev => ({ ...prev, [groupName]: v.value }))}
                      style={{ padding: '6px 14px', border: `1.5px solid ${isSelected ? C.primary : C.border}`, borderRadius: 6, background: isSelected ? '#FEF3ED' : 'white', color: isSelected ? C.primary : C.text, fontSize: 13, cursor: 'pointer', fontWeight: isSelected ? 600 : 400 }}>
                      {v.value}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Dostupnost */}
          <div style={{ marginBottom: 16, fontSize: 14 }}>
            {product.is_presale
              ? <span style={{ color: '#854F0B' }}>⏳ Předobjednávka</span>
              : inStock
                ? <span style={{ color: C.teal, fontWeight: 500 }}>✓ Skladem — expedice do 2 dnů</span>
                : <span style={{ color: '#A32D2D' }}>✗ Vyprodáno</span>}
          </div>

          {/* Množství + přidat */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
              <button onClick={() => setQty(q => Math.max(1, q-1))} style={{ padding: '10px 14px', border: 'none', background: 'white', cursor: 'pointer', fontSize: 16 }}>−</button>
              <span style={{ padding: '0 12px', fontWeight: 500, fontSize: 15 }}>{qty}</span>
              <button onClick={() => setQty(q => q+1)} style={{ padding: '10px 14px', border: 'none', background: 'white', cursor: 'pointer', fontSize: 16 }}>+</button>
            </div>
            <button onClick={addToCart} disabled={!inStock && !product.is_presale}
              style={{ flex: 1, padding: '11px 0', background: added ? C.teal : (inStock || product.is_presale ? C.primary : '#e0e0e0'), color: inStock || product.is_presale ? 'white' : '#aaa', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 500, cursor: inStock || product.is_presale ? 'pointer' : 'default', transition: 'background 0.2s' }}>
              {added ? '✓ Přidáno do košíku' : (!inStock && !product.is_presale ? 'Vyprodáno' : `Přidat do košíku · ${fmt((product.price_display || product.price_sell) * qty)}`)}
            </button>
          </div>

          <Link to="/shop/kosik" style={{ display: 'block', textAlign: 'center', fontSize: 13, color: C.muted, textDecoration: 'underline', marginBottom: 16 }}>Přejít do košíku</Link>

          <div style={{ fontSize: 13, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
            Doprava zdarma od {fmt(settings?.free_shipping || 1500)} · 14 dní na vrácení
          </div>
        </div>
      </div>

      {/* Popis */}
      {product.description && (
        <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${C.border}` }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Popis produktu</h2>
          <div style={{ fontSize: 14, lineHeight: 1.7, color: '#444', maxWidth: 720, whiteSpace: 'pre-wrap' }}>{product.description}</div>
        </div>
      )}

      {/* Hodnocení */}
      {product.reviews?.length > 0 && (
        <div style={{ marginTop: 40, paddingTop: 32, borderTop: `1px solid ${C.border}` }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Hodnocení zákazníků</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
            {product.reviews.map(r => (
              <div key={r.id} style={{ background: '#fafafa', borderRadius: 8, padding: '14px 16px', border: `0.5px solid ${C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{r.customer_name}</span>
                  <Stars rating={r.rating} />
                </div>
                {r.text && <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>{r.text}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Související produkty */}
      {product.related?.length > 0 && (
        <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${C.border}` }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Podobné produkty</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
            {product.related.map(r => (
              <Link key={r.id} to={`/shop/${r.slug}`} style={{ textDecoration: 'none', color: 'inherit', background: 'white', borderRadius: 12, border: `0.5px solid ${C.border}`, overflow: 'hidden', display: 'block' }}>
                <div style={{ height: 140, background: '#f7f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {r.image_url ? <img src={r.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 40 }}>📦</span>}
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{r.name}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>{fmt(r.price_display || r.price_sell)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
